// Mål-verktøy (Range & Bearing): klikk punkt-for-punkt, live avstand + peiling
// under musa. Escape/høyreklikk AVBRYTER (kaster), dobbeltklikk/Enter FULLFØRER
// (beholder målingen på kartet).
//
// ⚠ FIRE FEIL SOM GJORDE VERKTØYET UBRUKELIG, OG HVA SOM ER GJORT:
//
// 0. «Det er ikke mulig å slette avstandsmålinger.» `nullstill()` fantes, var
//    dokumentert som «kobles til «Slett alle»» — og ble så aldri koblet til noe.
//    Verktøyet kunne altså legge målinger PÅ kartet, men ikke ta dem AV.
//    Søsterverktøyene hadde alle sin: `draw-clear`, `axis-clear`, `unit-clear`,
//    `fire-clear`. Måleren var den eneste uten.
//    Verre: den frosne etiketten har et lukkekryss, og det var den eneste
//    knappen som SÅ ut som sletting. Den fjernet bare etiketten — linja og
//    punktene ble stående, nå uten noe som forklarte hva tallet var. Den ene
//    handlingen operatøren fant gjorde altså målingen både uforklart og
//    umulig å bli kvitt. Nå følger geometrien etiketten, og «Slett målinger»
//    finnes i verktøydokken og kommandopaletten.
//
// 1. «Dobbeltklikk kastet hele målingen.» `onFinish` pekte rett på `stop()`, som
//    tømmer kilden. Å fullføre og å avbryte var altså SAMME handling, og den ene
//    lovlige veien ut av verktøyet slettet arbeidet. Nå er `fullfor()` og
//    `avbryt()` to forskjellige ting, og bare den siste kaster noe.
//
// 2. «Ingen linje ble tegnet.» Lagene ble lagt tidlig, og HV-kommuneflata
//    (`hv-kom-fill`, 42 % rødt) legges senere — altså OVER. En 2 px stiplet
//    gullinje under en fyllflate finnes, men kan ikke ses. `start()` løfter nå
//    verktøylagene til topps.
//
// 3. «Et løsrevet tall 177.19 km ved siden av, uten forklaring.» Det var
//    RUTE-verktøyets popup: ingen ting slo av det andre verktøyet. Nå eies
//    kartklikkene av ett verktøy om gangen (`Kartmodus`).
//
// I tillegg: totalen vises fra FØRSTE segment (ikke først fra det tredje
// punktet), og hvert tall har en etikett. Et tall uten etikett i et
// beslutningsverktøy er ikke informasjon, det er støy.
import maplibregl, { Map as MlMap, GeoJSONSource, LngLat } from 'maplibre-gl';
import distance from '@turf/distance';
import bearing from '@turf/bearing';
import { point } from '@turf/helpers';
import { featureCollection, type GeoFeature } from '@skade/contracts';
import type { Kartmodus } from '../map/kartmodus.js';
import { hevVerktoylag, naarKartetErKlart } from '../map/verktoylag.js';

export const MEASURE_ID = 'measure';
const LAG = ['measure-line', 'measure-pts'] as const;

/** Ett fullført mål: geometrien beholdes, og etiketten sier hva tallet er. */
export interface FrossentMaal {
  /**
   * Stabil id. Finnes for at målinga skal kunne stå som et OBJEKT i høyrepanelet
   * med sin egen fjern-vei — før dette var den bare synlig som en etikett i
   * kartet, og «hvilken av de tre målingene er dette» kunne bare besvares ved å
   * peke i kartet.
   */
  id: string;
  feats: GeoFeature[];
  /** Total kjedelengde i meter — undertittelen i objektlista. */
  lengdeM: number;
  /** Etikettpunktet (siste punkt i kjeden) — objektlistas `flyTo`-mål. */
  senter: { lon: number; lat: number };
  /** Lukker etiketten som hører til denne målinga. */
  lukk: () => void;
}

/**
 * Bokføringa av fullførte målinger, skilt ut fra kartet slik at de to reglene
 * som ellers ville feilet stille kan testes uten DOM (samme grep som
 * `ui/windows.ts`; testene her kjører i node, ikke jsdom):
 *
 *   1. Å lukke ÉN etikett skal fjerne NØYAKTIG den målinga — ikke den første i
 *      lista, ikke alle. Uten identitet per måling ville to målinger med samme
 *      lengde vært umulige å skille.
 *   2. «Slett alle» lukker hver etikett, og hver lukking melder tilbake hit via
 *      popup-ens `close`-hendelse. Uten `rydder`-vakta ville tilbakemeldingene
 *      splice-et i lista mens løkka gikk gjennom den — annenhver måling ville
 *      overlevd en «slett alle».
 */
export class MaalingsRegister {
  private frosne: FrossentMaal[] = [];
  /** Sann mens vi rydder selv, så `fjern()` ikke muterer lista under løkka. */
  private rydder = false;

  get antall(): number { return this.frosne.length; }

  alleFeats(): GeoFeature[] { return this.frosne.flatMap((f) => f.feats); }

  /** Målingene slik de er, til objektlista. */
  liste(): readonly FrossentMaal[] { return this.frosne; }

  leggTil(maal: FrossentMaal): void { this.frosne.push(maal); }

  /** Fjern ÉN måling ved id (fra objektlista). Lukker også etiketten. */
  fjernId(id: string): boolean {
    const m = this.frosne.find((f) => f.id === id);
    if (!m) return false;
    // `lukk()` fyrer popup-ens `close`, som kaller `fjern(maal)`.
    m.lukk();
    // Belte og bukseseler: uten en close-hendelse (kart uten popup) ville målinga
    // blitt stående i lista mens etiketten var borte. `fjern` er idempotent.
    this.fjern(m);
    return true;
  }

  /** Fjern ÉN måling. Returnerer om noe faktisk ble fjernet. */
  fjern(maal: FrossentMaal): boolean {
    if (this.rydder) return false;
    const i = this.frosne.indexOf(maal);
    if (i < 0) return false;
    this.frosne.splice(i, 1);
    return true;
  }

  /**
   * Fjern alle. Returnerer antallet som ble fjernet, slik at den som kaller kan
   * si «ingen målinger å slette» framfor å se ut som den gjorde noe.
   */
  nullstill(): number {
    const antall = this.frosne.length;
    this.rydder = true;
    try { for (const f of this.frosne) f.lukk(); } finally { this.rydder = false; }
    this.frosne = [];
    return antall;
  }
}

export class MeasureTool {
  private active = false;
  private pts: LngLat[] = [];
  private marker?: maplibregl.Popup;
  /** Fullførte målinger. Beholdes til de slettes — de er svaret operatøren ba om. */
  private frosne = new MaalingsRegister();
  /** Løpenummer for målings-id. Monotont, så en slettet id ikke gjenbrukes. */
  private maalSeq = 0;

  constructor(private map: MlMap, private modus?: Kartmodus) {
    naarKartetErKlart(map, () => this.initLayers());
    this.modus?.registrer({ id: MEASURE_ID, navn: 'MÅLER', stopp: () => this.avbryt() });
  }

  private initLayers() {
    if (this.map.getSource('measure')) return;
    this.map.addSource('measure', { type: 'geojson', data: featureCollection([]) });
    this.map.addLayer({
      id: 'measure-line', type: 'line', source: 'measure',
      paint: { 'line-color': '#d4a03a', 'line-width': 2, 'line-dasharray': [2, 1.5] },
    });
    this.map.addLayer({
      id: 'measure-pts', type: 'circle', source: 'measure',
      filter: ['==', '$type', 'Point'],
      paint: { 'circle-radius': 4, 'circle-color': '#d4a03a', 'circle-stroke-color': '#0a0c0a', 'circle-stroke-width': 2 },
    });
  }

  toggle() { this.active ? this.avbryt() : this.start(); }

  start() {
    if (this.active) return; // guard mot dobbelt-registrering av lyttere
    // Ta kartet FØR vi lytter: taOver stopper de andre verktøyene, og deres
    // stop() rydder sine egne lyttere. Motsatt rekkefølge ville rekkefølgen på
    // lyttere avgjøre hvem som fikk klikket.
    this.modus?.taOver(MEASURE_ID);
    this.active = true;
    this.pts = [];
    // Verktøylagene må ligge over kartets egne lag, ellers tegner vi i det blinde.
    this.initLayers();
    hevVerktoylag(this.map, LAG);
    this.map.getCanvas().style.cursor = 'crosshair';
    this.map.on('click', this.onClick);
    this.map.on('mousemove', this.onMove);
    this.map.on('dblclick', this.onDblClick);
    this.map.on('contextmenu', this.onContext);
    this.map.doubleClickZoom.disable(); // dblklikk = fullfør, ikke zoom
    window.addEventListener('keydown', this.onKey);
    this.marker = new maplibregl.Popup({ closeButton: false, closeOnClick: false, className: 'measure-popup' });
  }

  /** Forlat måle-modus. Rører IKKE fullførte målinger. */
  private forlat() {
    if (!this.active) return;
    this.active = false;
    this.pts = [];
    this.map.getCanvas().style.cursor = '';
    this.map.off('click', this.onClick);
    this.map.off('mousemove', this.onMove);
    this.map.off('dblclick', this.onDblClick);
    this.map.off('contextmenu', this.onContext);
    this.map.doubleClickZoom.enable();
    window.removeEventListener('keydown', this.onKey);
    this.marker?.remove();
    this.marker = undefined;
    this.modus?.frigi(MEASURE_ID);
  }

  /** AVBRYT: kast den påbegynte målinga. Fullførte målinger står. */
  avbryt() {
    const hadde = this.pts.length > 0;
    this.forlat();
    this.render();
    return hadde;
  }

  /**
   * FULLFØR: fest målinga på kartet med en varig etikett, og gå ut av modus.
   * Returnerer total i meter, eller null om det ikke var nok punkter.
   *
   * Merk at et dobbeltklikk også har gitt et vanlig `click` først, så det siste
   * punktet er allerede lagt til når vi kommer hit — vi skal ikke legge det på
   * nytt fra dblclick-eventen.
   */
  fullfor(): number | null {
    if (this.pts.length < 2) {
      // Ett punkt er ikke en måling. Si det, i stedet for å legge igjen en
      // enslig prikk operatøren tror er et resultat.
      this.avbryt();
      return null;
    }
    const coords = this.pts.map((p) => [p.lng, p.lat] as [number, number]);
    const total = kjedeLengde(coords);
    const siste = coords[coords.length - 1];
    const feats: GeoFeature[] = [
      { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: { frossen: true } },
      ...coords.map<GeoFeature>((c) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: { frossen: true } })),
    ];
    const popup = new maplibregl.Popup({ closeButton: true, closeOnClick: false, className: 'measure-popup measure-popup-frossen' })
      .setLngLat({ lng: siste[0], lat: siste[1] })
      .setHTML(etikett([
        ['TOTALT', fmt(total)],
        ['BEN', String(coords.length - 1)],
      ]))
      .addTo(this.map);
    this.maalSeq += 1;
    const maal: FrossentMaal = {
      id: `m${this.maalSeq}`,
      feats,
      lengdeM: total,
      senter: { lon: siste[0], lat: siste[1] },
      lukk: () => popup.remove(),
    };
    // Lukkekrysset på etiketten er den slette-affordansen operatøren FINNER.
    // Uten denne lyttera fjernet den bare etiketten, mens linja og punktene ble
    // stående igjen uten noe som forklarte dem — geometrien må følge etiketten.
    popup.on('close', () => { if (this.frosne.fjern(maal)) this.render(); });
    this.frosne.leggTil(maal);
    this.forlat();
    this.render();
    this.onEndring?.();
    return total;
  }

  /**
   * Fjern alle fullførte målinger. Returnerer antallet, slik at menyvalget kan
   * si fra når det ikke var noe å slette i stedet for å gjøre ingenting stille.
   */
  nullstill(): number {
    const antall = this.frosne.nullstill();
    this.render();
    this.onEndring?.();
    return antall;
  }

  /** Antall fullførte målinger på kartet (til test og til statuslinja). */
  get antallFullforte(): number { return this.frosne.antall; }
  get erAktiv(): boolean { return this.active; }

  /**
   * Meldes ved enhver endring i settet av fullførte målinger, så objektlista kan
   * tegnes på nytt. Uten den sto en fersk måling i kartet men ikke i lista.
   */
  onEndring?: () => void;

  /** Fullførte målinger, til objektlista i høyrepanelet. */
  maalinger(): readonly FrossentMaal[] { return this.frosne.liste(); }

  /** Fjern ÉN måling (fra objektlista). Returnerer om noe ble fjernet. */
  fjernMaaling(id: string): boolean {
    const ok = this.frosne.fjernId(id);
    if (ok) { this.render(); this.onEndring?.(); }
    return ok;
  }

  private onClick = (e: maplibregl.MapMouseEvent) => {
    this.pts.push(e.lngLat);
    this.render(e.lngLat);
  };
  private onMove = (e: maplibregl.MapMouseEvent) => {
    if (this.pts.length) this.render(e.lngLat);
  };
  private onDblClick = (e: maplibregl.MapMouseEvent) => {
    // Uten dette panorerer/zoomer kartet på dobbeltklikket i tillegg.
    e.preventDefault();
    this.fullfor();
  };
  private onContext = (e: maplibregl.MapMouseEvent) => { e.preventDefault(); this.avbryt(); };
  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); this.avbryt(); }
    else if (e.key === 'Enter') { e.preventDefault(); this.fullfor(); }
  };

  private render(cursor?: LngLat) {
    const kilde = this.map.getSource('measure') as GeoJSONSource | undefined;
    if (!kilde) return; // stil ikke klar ennå — neste render tar det
    const frosneFeats = this.frosne.alleFeats();
    if (!this.active || !cursor || !this.pts.length) {
      kilde.setData(featureCollection(frosneFeats) as never);
      return;
    }

    const chain = [...this.pts, cursor];
    const coords = chain.map((p) => [p.lng, p.lat] as [number, number]);
    const feats: GeoFeature[] = [
      ...frosneFeats,
      { type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} },
      ...this.pts.map<GeoFeature>((p) => ({
        type: 'Feature', geometry: { type: 'Point', coordinates: [p.lng, p.lat] }, properties: {},
      })),
    ];
    kilde.setData(featureCollection(feats) as never);

    // Live-etikett: siste segment + total. ALLE tall har etikett — «0 m · 0°» uten
    // ord er ikke lesbart for noen som ser verktøyet første gang.
    const a = this.pts[this.pts.length - 1];
    const segM = distance(point([a.lng, a.lat]), point([cursor.lng, cursor.lat]), { units: 'meters' });
    const brg = (bearing(point([a.lng, a.lat]), point([cursor.lng, cursor.lat])) + 360) % 360;
    const total = kjedeLengde(coords);
    const rader: Array<[string, string]> = [
      ['SEGMENT', fmt(segM)],
      ['PEILING', `${brg.toFixed(0).padStart(3, '0')}°`],
    ];
    // Totalen vises fra og med andre punkt — altså så snart den er noe ANNET enn
    // segmentet. Før: først fra tredje punkt, som gjorde at operatøren måtte
    // gjette om tallet var summen eller bare siste ben.
    if (this.pts.length >= 2) rader.push(['TOTALT', fmt(total)]);
    rader.push(['', 'Dobbeltklikk/Enter fullfører · Esc avbryter']);
    this.marker?.setLngLat(cursor).setHTML(etikett(rader)).addTo(this.map);
  }
}

/** Sum av alle ben i en kjede, i meter. */
export function kjedeLengde(coords: readonly [number, number][]): number {
  let sum = 0;
  for (let i = 1; i < coords.length; i++) {
    sum += distance(point(coords[i - 1]), point(coords[i]), { units: 'meters' });
  }
  return sum;
}

/**
 * Avstand for lesing i felt: meter under 1 km (heltall — centimeter er en løgn
 * om nøyaktigheten), km over. Tusenskille som hardt mellomrom, ikke
 * `toLocaleString`: sistnevnte gir forskjellig utfall på forskjellige maskiner,
 * og et tall som endrer utseende med nettleserens språk kan ikke leses opp over
 * samband med tillit.
 */
export function fmt(m: number): string {
  if (!Number.isFinite(m)) return '—';
  // Rund FØRST, velg enhet ETTERPÅ. Motsatt rekkefølge ga «1 000 m» for 999,6 m
  // — altså et km-tall skrevet i meter, som er nettopp den tvetydigheten
  // enhetsvalget skal fjerne.
  const rundet = Math.round(m);
  if (rundet >= 1000) return `${(m / 1000).toFixed(2)} km`;
  return `${grupper(rundet)} m`;
}

function grupper(n: number): string {
  const s = String(Math.abs(n));
  let ut = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) ut += ' ';
    ut += s[i];
  }
  return (n < 0 ? '-' : '') + ut;
}

/** Etikett/verdi-rader. Ren tekst inn, ingen brukerdata — trygt mot injeksjon. */
function etikett(rader: ReadonlyArray<readonly [string, string]>): string {
  return rader
    .map(([k, v]) => (k
      ? `<span class="measure-k">${k}</span> <b class="measure-v">${v}</b>`
      : `<span class="measure-hint">${v}</span>`))
    .join('<br>');
}
